"use client";

import dynamic from "next/dynamic";
import type { ComponentType, CSSProperties } from "react";
import { cn } from "@/lib/utils";
import type { RevenueToolSlug } from "@/lib/revenue-tool-pages";
import { stageWidth, TOOL_DEMOS, type ToolDemoSpec } from "./registry";

const WhaleWatchDemo = dynamic(() => import("./whale-watch").then((m) => m.WhaleWatchDemo), { ssr: false });

const DEMOS = {
  "whale-watch": WhaleWatchDemo,
  "prediction-markets": WhaleWatchDemo,
  "esports-discrepancies": dynamic(() => import("./esports-discrepancies").then((m) => m.EsportsDiscrepanciesDemo), { ssr: false }),
  "steam-chaser": dynamic(() => import("./steam-chaser").then((m) => m.SteamChaserDemo), { ssr: false }),
  arbitrage: dynamic(() => import("./arbitrage").then((m) => m.ArbitrageDemo), { ssr: false }),
  "low-hold": dynamic(() => import("./low-hold").then((m) => m.LowHoldDemo), { ssr: false }),
  "ev-sniper": dynamic(() => import("./ev-sniper").then((m) => m.EvSniperDemo), { ssr: false }),
  "odds-screen": dynamic(() => import("./odds-screen").then((m) => m.OddsScreenDemo), { ssr: false }),
  "ev-props-optimizer": dynamic(() => import("./fantasy-props").then((m) => m.FantasyPropsDemo), { ssr: false }),
  "prizepicks-optimizer": dynamic(() => import("./fantasy-props").then((m) => m.PrizePicksDemo), { ssr: false }),
  "sports-betting-optimizer": dynamic(() => import("./sports-betting-optimizer").then((m) => m.SportsBettingOptimizerDemo), { ssr: false }),
} satisfies Record<RevenueToolSlug, ComponentType>;

export function ToolDemoFigure({ slug }: { slug: RevenueToolSlug }) {
  const spec: ToolDemoSpec = TOOL_DEMOS[slug];
  const Demo = DEMOS[slug];
  const stage = stageWidth(spec);
  const size = {
    "--tool-demo-wide": `${stage} / ${spec.height}`,
    "--tool-demo-span": `${stage / spec.width}`,
    "--tool-demo-compact": `${spec.compact.width} / ${spec.compact.height}`,
  } as CSSProperties;
  return (
    <figure className="@container/tool-demo w-full" style={{ maxWidth: spec.width }}>
      <div
        role="img"
        aria-label={spec.label}
        className={cn(
          "aspect-(--tool-demo-wide) w-[calc(100%*var(--tool-demo-span))] overflow-hidden @max-[439.5px]/tool-demo:aspect-(--tool-demo-compact) @max-[439.5px]/tool-demo:w-full",
          spec.frame === "bleed" && "@min-[439.5px]/tool-demo:[mask-image:linear-gradient(to_right,#000_calc(100%-96px),transparent)]",
        )}
        style={size}
      >
        <Demo />
      </div>
      <figcaption className={cn("mt-3 text-[11px] font-medium text-muted-foreground", spec.frame === "bleed" ? "text-left" : "text-center")}>
        Illustration · Example prices, not live odds
      </figcaption>
    </figure>
  );
}
